// src/hooks/useCurrentCrmRole.ts
//
// Resolves the current effective CRM in-app role for the signed-in user.
//
// Precedence:
//   1. Active Testing Mode override (from Central) — lets QA simulate roles.
//   2. The user's real Central role on the active workspace.
//
// The mapping from Central (role + templateId/capabilityPreset) → CRM role
// is performed by `getCrmRoleForCentralRole` in the shared `@levelup/crm-roles`
// package, which is the single source of truth for CRM role semantics.

import { useMemo } from 'react';
import {
  CRM_ROLES,
  MEMBER_PRESET_TEMPLATE_IDS,
  getCrmRoleForCentralRole,
  type CentralRole,
  type CrmRoleConfig,
  type CrmRoleKey,
} from '@levelup/crm-roles';
import { useAuth } from './useAuth';
import { useAppSelector } from '../store/store';
import { selectBootCurrentWorkspace } from '../store/slices/bootContextSlice';
import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';
import { useGetTestingModeStatusQuery } from '../api/centralTestingModeApi';
import { CRM_TESTING_VARIANT_STORAGE_KEY } from '../components/testingMode/TestingModePanelContent';

/**
 * Read the CRM-local member-variant overlay persisted by the Testing Mode
 * panel. Central's staging does not have CRM-specific capability presets
 * registered, so when `role === 'member'` the panel stores the picked
 * variant (agent / coordinator / analyst) in localStorage and we read it
 * here to drive the sidebar gate.
 */
function readMemberVariantOverlay(): CrmRoleKey | null {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(CRM_TESTING_VARIANT_STORAGE_KEY);
  return stored === 'agent' || stored === 'coordinator' || stored === 'analyst'
    ? stored
    : null;
}

/**
 * Central's Testing Mode `capabilityPreset` may be either a raw UUID
 * (`00000000-0000-0000-0000-000000000201`) or a short key (`agent`). Map
 * both back to the canonical member preset templateId for resolver input.
 */
function normalizeCapabilityPreset(preset: string | undefined | null): string | null {
  if (!preset) return null;
  // Already a UUID registered with Central.
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidPattern.test(preset)) return preset;
  // Short alias (e.g. 'agent' / 'coordinator' / 'analyst') — look up the UUID.
  const alias = preset.toLowerCase() as keyof typeof MEMBER_PRESET_TEMPLATE_IDS;
  return MEMBER_PRESET_TEMPLATE_IDS[alias] ?? null;
}

export function useCurrentCrmRole(): CrmRoleConfig {
  const { user } = useAuth();
  // Testing Mode is a Central-authenticated, browser-direct feature: the API
  // base query attaches `Authorization: Bearer ${centralAuth.accessToken}`,
  // and Central rejects the call without a Bearer. For RP (BFF / `crm_sid`-
  // cookie) users no Bearer ever enters the browser, so the request is
  // structurally guaranteed to 401. `skip` the query in that case — it
  // removes a guaranteed 401 from the Network panel without changing
  // behavior for legacy / Central-direct browser-Bearer users.
  const centralAccessToken = useAppSelector(selectCentralAccessToken);
  const { data: testingStatus } = useGetTestingModeStatusQuery(undefined, {
    skip: !centralAccessToken,
  });
  // `bootContext.currentWorkspace` is populated from Central's
  // `GET /api/suite/switch-context` and is the freshest view of the user's
  // role on the active workspace. `user.role` is stamped at login and may
  // lag; we prefer the boot-context value so Testing Mode overrides and
  // role changes surface immediately.
  const bootCurrentWorkspace = useAppSelector(selectBootCurrentWorkspace);
  const bootRole = bootCurrentWorkspace?.role;

  return useMemo<CrmRoleConfig>(() => {
    const override = testingStatus?.active ? testingStatus.override : null;

    // Helper: resolve a Central core role to a CRM role config. For
    // `member`, the CRM-local variant overlay (set by the Testing Mode
    // panel) wins, then Central's capabilityPreset, then the default
    // Agent fallback — so the UI label stays consistent with the sidebar
    // gate whether or not Central's override call succeeded.
    const resolve = (centralRole: CentralRole, preset?: string | null): CrmRoleConfig => {
      if (centralRole === 'member') {
        const overlay = readMemberVariantOverlay();
        if (overlay) return CRM_ROLES[overlay];
      }
      return getCrmRoleForCentralRole(centralRole, normalizeCapabilityPreset(preset));
    };

    // 1) Testing Mode override (Central authoritative for the core role)
    if (override?.role) {
      const centralRole = override.role.toLowerCase() as CentralRole;
      if (
        centralRole === 'owner' ||
        centralRole === 'admin' ||
        centralRole === 'member' ||
        centralRole === 'viewer'
      ) {
        return resolve(centralRole, override.capabilityPreset);
      }
    }

    // 2) Central boot-context role (freshest Central-direct value)
    const fromBoot = bootRole ? bootRole.toLowerCase() : null;
    if (fromBoot === 'owner' || fromBoot === 'admin' || fromBoot === 'member' || fromBoot === 'viewer') {
      return resolve(fromBoot, null);
    }

    // 3) Login-stamped role (legacy fallback)
    const raw = (user?.role ?? 'viewer').toString().toLowerCase() as CentralRole;
    if (raw === 'owner' || raw === 'admin' || raw === 'member' || raw === 'viewer') {
      return resolve(raw, null);
    }

    // Defensive fallback — matches `getCrmRoleForCentralRole`'s own fallback.
    return CRM_ROLES.viewer;
  }, [testingStatus?.active, testingStatus?.override, bootRole, user?.role]);
}
